import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Layout, Button, message, Card, Typography, Modal, Tag, Descriptions, List, Input, Form, Row, Col } from 'antd';
import { LogoutOutlined, ScanOutlined, UserOutlined, UserAddOutlined, MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiService } from '../services/api';
import { getCurrentUser, getUserRole } from '../utils/auth';
import { AgentInfo, AgentEventInfo } from '../types/auth';
import QRScanner from './QrScanner';

const { Header, Content } = Layout;
const { Title } = Typography;

const MerchantEvent: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUser = getCurrentUser();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const deepLinkAgentIdRef = useRef<string | null>(null);
  
  // Проверяем роль пользователя - только мерчанты могут быть здесь
  useEffect(() => {
    const userRole = getUserRole();
    if (userRole !== 'merchant') {
      navigate('/admin/dashboard', { replace: true });
    }
  }, [navigate]);

  // Отслеживаем изменение размера окна
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const [isScannerModalVisible, setIsScannerModalVisible] = useState(false);
  const [isAgentModalVisible, setIsAgentModalVisible] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<AgentInfo | null>(null);
  const [agentModalLoading, setAgentModalLoading] = useState(false);
  const [isVisitorModalVisible, setIsVisitorModalVisible] = useState(false);
  const [isAddingVisitors, setIsAddingVisitors] = useState(false);
  const [visitorForm] = Form.useForm();
  const scannerRef = useRef<{ stopScanner: () => void }>(null);

  const handleLogout = async () => {
    try {
      await apiService.logoutUser();
      navigate('/login');
    } catch (error) {
      message.error('Logout failed');
    }
  };

  const handleScan = useCallback(async (result: string) => {
    try {
      // Extract agent ID from QR code result
      const agentId = result.trim();
      
      // Get agent information
      setAgentModalLoading(true);
      const agentInfo = await apiService.getAgentInfo(agentId);
      
      // Проверяем состояние агента и показываем предупреждения
      if (agentInfo.agent.state === 'deleted') {
        message.warning('Agent is deleted. Actions are limited.');
      } else if (agentInfo.agent.state === 'blocked') {
        message.warning('Agent is blocked. Some actions may be limited.');
      }
      
      setSelectedAgent(agentInfo);
      setIsAgentModalVisible(true);
      setIsScannerModalVisible(false);
      
      // Stop scanner
      if (scannerRef.current) {
        scannerRef.current.stopScanner();
      }
      
      message.success('Agent found successfully!');
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to find agent');
    } finally {
      setAgentModalLoading(false);
    }
  }, []);

  // Deep link: /merchant/event?agentId=XXXXX
  useEffect(() => {
    if (getUserRole() !== 'merchant') {
      return;
    }

    const agentId = searchParams.get('agentId')?.trim();
    if (!agentId) {
      deepLinkAgentIdRef.current = null;
      return;
    }

    if (deepLinkAgentIdRef.current === agentId) {
      return;
    }

    deepLinkAgentIdRef.current = agentId;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('agentId');
    setSearchParams(nextParams, { replace: true });
    void handleScan(agentId);
  }, [searchParams, setSearchParams, handleScan]);

  const handleError = (error: Error) => {
    message.error(`Scanner error: ${error.message}`);
  };

  const handleScannerModalClose = () => {
    if (scannerRef.current) {
      scannerRef.current.stopScanner();
    }
    setIsScannerModalVisible(false);
  };

  const handleAgentModalClose = () => {
    setIsAgentModalVisible(false);
    setSelectedAgent(null);
  };

  const handleAgentVisited = () => {
    visitorForm.resetFields();
    visitorForm.setFieldsValue({
      visitors: [{ name: '', phone: '' }]
    });
    setIsVisitorModalVisible(true);
  };

  const handleVisitorModalClose = () => {
    setIsVisitorModalVisible(false);
    visitorForm.resetFields();
    setIsAddingVisitors(false);
  };

  const handleAddVisitors = async (values: { visitors: Array<{ name: string; phone: string }> }) => {
    if (!selectedAgent) return;
    
    // Проверяем, не удален ли агент
    if (selectedAgent.agent.state === 'deleted') {
      message.error('Cannot add visitors to deleted agent');
      return;
    }
    
    // Проверяем, не заблокирован ли агент
    if (selectedAgent.agent.state === 'blocked') {
      message.error('Cannot add visitors to blocked agent');
      return;
    }
    
    setIsAddingVisitors(true);
    
    try {
      // Filter out visitors with empty names or phones
      const validVisitors = values.visitors.filter(visitor => visitor.name?.trim() || visitor.phone?.trim());
      
      const eventData = {
        guests: validVisitors
      };
      
      await apiService.addAgentEvent(selectedAgent.agent.id, eventData);
      message.success('Visitors added successfully');
      
      const updatedAgentInfo = await apiService.getAgentInfo(selectedAgent.agent.id);
      setSelectedAgent(updatedAgentInfo);
      
      setIsVisitorModalVisible(false);
      visitorForm.resetFields();
    } catch (error: any) {
      message.error('Failed to add visitors');
    } finally {
      setIsAddingVisitors(false);
    }
  };

  const formatDateTime = (dateTime: string) => {
    return new Date(dateTime).toLocaleString();
  };

  // Получаем имя мерчанта с заглавной буквы
  const getMerchantName = () => {
    if (!currentUser?.username) return 'Merchant Event';
    const username = currentUser.username;
    return username.charAt(0).toUpperCase() + username.slice(1);
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{ 
        background: '#fff', 
        padding: isMobile ? '0 16px' : '0 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        <Title level={3} style={{ 
          margin: 0,
          fontSize: isMobile ? '18px' : '24px'
        }}>
          {getMerchantName()}
        </Title>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {currentUser && !isMobile && (
            <span style={{ color: '#666' }}>
              Welcome, {currentUser.username}
            </span>
          )}
          <Button 
            type="text" 
            icon={<LogoutOutlined />} 
            onClick={handleLogout}
          >
            Logout
          </Button>
        </div>
      </Header>
      
      <Content style={{ 
        margin: isMobile ? '16px 8px' : '24px 16px', 
        padding: isMobile ? 16 : 24, 
        background: '#fff' 
      }}>
        <Card title="QR Scanner" style={{ maxWidth: 800, margin: '0 auto' }}>
          <p>Scan QR codes to quickly access agent information.</p>
          <Button 
            type="primary" 
            icon={<ScanOutlined />}
            size="large"
            onClick={() => setIsScannerModalVisible(true)}
            block={isMobile}
          >
            Scan QR Code
          </Button>
        </Card>
      </Content>

      {/* QR Scanner Modal */}
      <Modal
        title="QR Code Scanning"
        open={isScannerModalVisible}
        onCancel={handleScannerModalClose}
        footer={null}
        width={isMobile ? '90vw' : 600}
        bodyStyle={{ 
          padding: '16px', 
          height: isMobile ? '300px' : '400px' 
        }}
      >
        <QRScanner 
          ref={scannerRef}
          onResult={handleScan} 
          onError={handleError} 
          isActive={isScannerModalVisible}
        />
      </Modal>

      {/* Agent Details Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <UserOutlined />
            Agent Details
          </div>
        }
        open={isAgentModalVisible}
        onCancel={handleAgentModalClose}
        footer={[
          <Button key="close" onClick={handleAgentModalClose}>
            Close
          </Button>
        ]}
        width={isMobile ? '95vw' : 800}
        loading={agentModalLoading}
      >
        {selectedAgent && (
          <div>
            <div style={{ 
              display: 'flex', 
              gap: 24, 
              marginBottom: 24,
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: isMobile ? 'center' : 'flex-start'
            }}>
              {/* Фотографии агента */}
              <div style={{ 
                flexShrink: 0,
                textAlign: 'center',
                order: window.innerWidth <= 768 ? 1 : 1
              }}>
                {(() => {
                  const agentImages = [
                    { imageSrc: selectedAgent.agent.imageUrl || selectedAgent.agent.imageLink, badge: undefined },
                    { imageSrc: selectedAgent.agent.reraImage, badge: 'Image from license' }
                  ]
                    .filter((entry): entry is { imageSrc: string; badge?: string } => Boolean(entry.imageSrc))
                    .filter((entry, index, array) => array.findIndex((candidate) => candidate.imageSrc === entry.imageSrc) === index);

                  if (agentImages.length === 0) {
                    return (
                      <div style={{ 
                        width: isMobile ? '200px' : '250px', 
                        height: isMobile ? '200px' : '250px',
                        border: '3px dashed #d9d9d9',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#fafafa'
                      }}>
                        <span style={{ 
                          color: '#999', 
                          fontStyle: 'italic',
                          fontSize: '16px',
                          textAlign: 'center'
                        }}>
                          No photo<br/>available
                        </span>
                      </div>
                    );
                  }

                  return (
                    <div>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 12,
                          alignItems: 'center',
                        }}
                      >
                        {agentImages.map(({ imageSrc, badge }, index) => (
                          <div key={`${imageSrc}-${index}`}>
                            <img 
                              src={imageSrc}
                              alt={`${selectedAgent.agent.name} ${index + 1}`}
                              style={{ 
                                width: isMobile ? '200px' : '250px', 
                                height: isMobile ? '200px' : '250px', 
                                objectFit: 'cover',
                                borderRadius: '12px',
                                cursor: 'pointer',
                                border: '3px solid #d9d9d9',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                              }}
                              onClick={() => {
                                Modal.info({
                                  title: `${selectedAgent.agent.name} Photo ${index + 1}`,
                                  content: (
                                    <div style={{ textAlign: 'center' }}>
                                      <img 
                                        src={imageSrc}
                                        alt={`${selectedAgent.agent.name} ${index + 1}`}
                                        style={{ 
                                          maxWidth: '100%', 
                                          maxHeight: '600px', 
                                          objectFit: 'contain',
                                          borderRadius: '8px'
                                        }}
                                      />
                                    </div>
                                  ),
                                  width: 800,
                                  okText: 'Close'
                                });
                              }}
                            />
                            {badge && (
                              <div style={{ marginTop: 6, fontSize: '12px', color: '#666', fontWeight: 500 }}>
                                {badge}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Информация об агенте */}
              <div style={{ 
                flex: 1,
                order: window.innerWidth <= 768 ? 2 : 2
              }}>
                <Descriptions 
                  title="Agent Information" 
                  bordered 
                  column={2}
                  size="small"
                  labelStyle={{ 
                    fontWeight: 'bold',
                    padding: '8px 12px',
                    backgroundColor: '#fafafa'
                  }}
                  contentStyle={{ 
                    padding: '8px 12px'
                  }}
                >
                  <Descriptions.Item label="Name" span={2}>
                    <span style={{ fontSize: '16px', fontWeight: '500' }}>
                      {selectedAgent.agent.name}
                    </span>
                  </Descriptions.Item>
                  {selectedAgent.agent.reraName && (
                    <Descriptions.Item label="License Name" span={2}>
                      <span style={{ fontSize: '14px' }}>
                        {selectedAgent.agent.reraName}
                      </span>
                    </Descriptions.Item>
                  )}
                  
                  <Descriptions.Item label="ID" span={2}>
                    <span style={{ 
                      fontFamily: 'monospace', 
                      fontSize: '13px',
                      backgroundColor: '#f5f5f5',
                      padding: '3px 6px',
                      borderRadius: '3px',
                      border: '1px solid #d9d9d9',
                      display: 'inline-block',
                      width: 'fit-content'
                    }}>
                      {selectedAgent.agent.id}
                    </span>
                  </Descriptions.Item>
                  
                  <Descriptions.Item label="License" span={2}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ 
                          fontFamily: 'monospace', 
                          fontSize: '13px',
                          backgroundColor: '#e6f7ff',
                          padding: '3px 6px',
                          borderRadius: '3px',
                          border: '1px solid #91d5ff',
                          display: 'inline-block',
                          width: 'fit-content',
                          color: '#1890ff'
                        }}>
                          {selectedAgent.agent.licenseId}
                        </span>
                        {selectedAgent.agent.reraVerified !== undefined && (
                          <Tag color={selectedAgent.agent.reraVerified ? 'green' : 'red'}>
                            {selectedAgent.agent.reraVerified ? 'Verified' : 'Not Verified'}
                          </Tag>
                        )}
                      </div>
                      {selectedAgent.agent.reraVerified === false && (
                        <div style={{ color: '#ff4d4f', fontSize: '12px', fontWeight: '500' }}>
                          Hostess visit required
                        </div>
                      )}
                    </div>
                  </Descriptions.Item>
                  
                  <Descriptions.Item label="Company" span={2}>
                    <span style={{ fontSize: '14px' }}>
                      {selectedAgent.agent.company}
                    </span>
                  </Descriptions.Item>
                  
                  <Descriptions.Item label="Email" span={2}>
                    <span style={{ fontSize: '14px', color: '#1890ff' }}>
                      {selectedAgent.agent.email}
                    </span>
                  </Descriptions.Item>
                  
                  <Descriptions.Item label="Phone" span={2}>
                    <span style={{ fontSize: '14px' }}>
                      {selectedAgent.agent.phone}
                    </span>
                  </Descriptions.Item>
                  
                  <Descriptions.Item label="Registration Date" span={2}>
                    <span style={{ fontSize: '13px', color: '#666' }}>
                      {formatDateTime(selectedAgent.agent.createdAt)}
                    </span>
                  </Descriptions.Item>
                  <Descriptions.Item label="State" span={2}>
                    {(() => {
                      const stateConfig = {
                        active: { color: 'green', text: 'Active' },
                        blocked: { color: 'red', text: 'Blocked' },
                        deleted: { color: 'gray', text: 'Deleted' }
                      };
                      const config = stateConfig[selectedAgent.agent.state as keyof typeof stateConfig] || { color: 'default', text: selectedAgent.agent.state };
                      return <Tag color={config.color}>{config.text}</Tag>;
                    })()}
                  </Descriptions.Item>
                </Descriptions>
              </div>
            </div>
            
            {/* Agent Status Block */}
            {(selectedAgent.agent.state === 'deleted' || selectedAgent.agent.state === 'blocked') && (
              <Card 
                style={{ 
                  marginTop: 16, 
                  marginBottom: 16,
                  borderColor: '#ff4d4f',
                  backgroundColor: '#fff2f0'
                }}
                size="small"
              >
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 12,
                  color: '#ff4d4f'
                }}>
                  <div style={{ 
                    fontSize: '18px', 
                    fontWeight: 'bold' 
                  }}>
                    {selectedAgent.agent.state === 'deleted' ? 'Agent Deleted' : 'Agent Blocked'}
                  </div>
                  {selectedAgent.agent.blockReason && (
                    <div style={{ 
                      fontSize: '14px',
                      color: '#666',
                      fontStyle: 'italic'
                    }}>
                      Reason: {selectedAgent.agent.blockReason}
                    </div>
                  )}
                </div>
              </Card>
            )}
            
            <div style={{ marginTop: 24 }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                marginBottom: 16,
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? 16 : 0
              }}>
                <h3>Events History ({selectedAgent.events.length})</h3>
                {selectedAgent.agent.state === 'blocked' ? (
                  <div style={{ color: '#ff4d4f', fontWeight: 'bold', fontSize: '16px' }}>
                    Agent Blocked
                  </div>
                ) : selectedAgent.notAvailableEventsUtil ? (
                  <div style={{ color: '#666', fontSize: '14px', fontStyle: 'italic' }}>
                    New events not available until {selectedAgent.notAvailableEventsUtil}
                  </div>
                ) : (
                  <Button
                    type="primary"
                    icon={<UserAddOutlined />}
                    onClick={handleAgentVisited}
                    disabled={selectedAgent.agent.reraVerified === false}
                  >
                    Agent Visited
                  </Button>
                )}
              </div>
              <List
                dataSource={selectedAgent.events}
                renderItem={(event: AgentEventInfo) => (
                  <List.Item>
                    <List.Item.Meta
                      title={
                        <div style={{ 
                          display: 'flex', 
                          gap: 8,
                          flexDirection: isMobile ? 'column' : 'row',
                          alignItems: isMobile ? 'flex-start' : 'center'
                        }}>
                          <Tag color="blue">{event.event.toUpperCase()}</Tag>
                          <span>{formatDateTime(event.dateTime)}</span>
                        </div>
                      }
                      description={
                        event.amount && event.amount > 0
                          ? `${event.merchantName ? `Merchant: ${event.merchantName} | ` : ''}Amount AED: ${event.amount}`
                          : (event.merchantName ? `Merchant: ${event.merchantName}` : 'No merchant information')
                      }
                    />
                  </List.Item>
                )}
                locale={{
                  emptyText: 'No events recorded for this agent'
                }}
              />
            </div>
          </div>
        )}
      </Modal>

      {/* Visitor Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <UserAddOutlined />
            Agent Visited
          </div>
        }
        open={isVisitorModalVisible}
        onCancel={handleVisitorModalClose}
        footer={[
          <Button key="cancel" onClick={handleVisitorModalClose} disabled={isAddingVisitors}>
            Cancel
          </Button>,
          <Button 
            key="submit" 
            type="primary" 
            onClick={() => visitorForm.submit()}
            loading={isAddingVisitors}
          >
            Add Visitors
          </Button>
        ]}
        width={isMobile ? '95vw' : 600}
      >
        <Form
          form={visitorForm}
          layout="vertical"
          onFinish={handleAddVisitors}
          initialValues={{
            visitors: [{ name: '', phone: '' }]
          }}
        >
          <Form.List name="visitors">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Card 
                    key={key} 
                    size="small" 
                    title={`Visitor ${name + 1}`}
                    style={{ marginBottom: 16 }}
                    extra={
                      fields.length > 1 ? (
                        <Button
                          type="text"
                          icon={<MinusCircleOutlined />}
                          onClick={() => remove(name)}
                          disabled={isAddingVisitors}
                        />
                      ) : null
                    }
                  >
                    <Row gutter={16}>
                      <Col span={isMobile ? 24 : 12}>
                        <Form.Item
                          {...restField}
                          name={[name, 'name']}
                          label="Name (Optional)"
                        >
                          <Input placeholder="Enter visitor name (optional)" disabled={isAddingVisitors} />
                        </Form.Item>
                      </Col>
                      <Col span={isMobile ? 24 : 12}>
                        <Form.Item
                          {...restField}
                          name={[name, 'phone']}
                          label="Phone (Optional)"
                        >
                          <Input placeholder="Enter phone number (optional)" disabled={isAddingVisitors} />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                ))}
                <Form.Item>
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                    disabled={isAddingVisitors}
                  >
                    Add Another Visitor
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>
    </Layout>
  );
};

export default MerchantEvent;

